/**
 * Recherche prédictive — module « états ».
 *
 * Responsabilité unique : arbitrer CE QUI EST AFFICHÉ dans le panneau. L'état A (les
 * raccourcis, au focus sans saisie), l'état B (les résultats d'une réponse), le
 * chargement, l'état vide et l'erreur. Il ne fait ni réseau (le fetcher), ni ARIA (le
 * combobox), ni stockage (les récentes) : il les orchestre.
 *
 * S'enregistre sous `window.MintPredictiveSearch.states`, chargé en `<script defer>`
 * AVANT `predictive-search.js` — l'ordre du document fait l'ordre d'exécution des
 * `defer`, comme pour les trois autres modules.
 *
 * ── Pourquoi un cinquième fichier plutôt que du code dans l'hôte ────────────────────
 * `predictive-search.js` tient le cycle de vie et rien d'autre, et il est à 20 lignes de
 * son plafond. Chaque responsabilité reste révocable seule : retirer ce fichier laisse un
 * panneau qui s'ouvre, se ferme et navigue au clavier, sans jamais interroger le réseau.
 * `create()` rend `null` si le markup manque — l'hôte se dégrade au lieu de lever.
 *
 * ── La règle qui commande tout le reste : ne jamais démasquer B avant la réponse ─────
 * Le module legacy démasquait le conteneur de résultats puis le remasquait quand la
 * réponse était vide, d'où un flash à chaque frappe non fructueuse. Ici, la bascule se
 * fait APRÈS insertion, jamais avant : pendant la requête, l'état affiché ne bouge pas et
 * seule la classe `is-loading` change sur le wrapper.
 *
 * ── Trois portes que ce module tient pour les autres ────────────────────────────────
 *   - **Q-S11, ne rien déclencher au chargement** : le champ est PRÉ-REMPLI sur
 *     `/search?q=…` (`value="{{ search.terms … }}"`) et le fetcher ne sait pas distinguer
 *     une frappe d'un `input` programmatique. Le garde est donc ici, et il est double :
 *     `state.term` naît avec la valeur du champ (une frappe qui ne change rien ne part
 *     pas), et un `input` non `isTrusted` est ignoré. Un `focus` ne déclenche JAMAIS de
 *     requête — l'ouverture montre l'état A, qui est rendu côté serveur.
 *   - **Le bandeau des récentes** (T26) est rendu et peuplé par son module, mais rien ne
 *     l'appelle : `render()` à chaque ouverture — c'est ce qui rattrape une écriture
 *     venue d'un autre onglet — et `add()` sur recherche aboutie.
 *   - **Ce qu'est une « recherche aboutie »** : une soumission (Entrée, ou le bouton
 *     « voir tous les résultats » que T21 route par `requestSubmit()`), et le re-jeu d'une
 *     ligne récente. PAS une réponse de suggestion : taper « cannondale » produit dix
 *     réponses, et les récentes retiendraient cinq préfixes du même mot.
 *
 * ── Deux pièges de visibilité, déjà payés dans ce thème ─────────────────────────────
 *   - `.hide` vaut `display:none!important` (`theme.css.liquid`) : elle n'est pas
 *     révocable par une feuille auteur, donc la visibilité ne passe JAMAIS par elle.
 *   - `el.hidden = true` ne masque rien dès qu'une feuille auteur pose un `display` sur
 *     l'élément (anti-patterns n°8 et n°20). Les deux nœuds d'état pilotés ici exigent
 *     leur garde CSS explicite — `[data-predictive-empty][hidden]` et
 *     `[data-predictive-results][hidden]` en `display: none` — qui appartient à T28.
 *
 * Chargé en asset séparé, jamais en bloc `javascript` de section : raison écrite en
 * tête de `predictive-search.js`.
 */
(function (window, document) {
  'use strict';

  var registry = (window.MintPredictiveSearch = window.MintPredictiveSearch || {});

  /** Posée sur le wrapper pendant la requête. Seule la feuille (T28) la lit. */
  var LOADING_CLASS = 'is-loading';

  function normalize(term) {
    return String(term == null ? '' : term).trim();
  }

  /**
   * @param {{ host: object, beforeInsert?: function }} options - `host` est l'élément
   *   `<predictive-search>`, qui porte déjà ses nœuds résolus et ses collaborateurs :
   *   les relire ici les dédoublerait. `beforeInsert` reçoit le résultat AVANT insertion,
   *   sur le document détaché.
   *   ⚠️ **AUCUN APPELANT NE LE PASSE AUJOURD'HUI** — point d'extension inutilisé, conservé
   *   parce qu'il est le seul endroit où l'on tient le document détaché juste avant qu'il ne
   *   soit peint. Et surtout : **ce n'est PAS là que le contexte FMD est recopié**, contrairement
   *   à ce que cette doc affirmait. T40 l'applique dans `predictive-search-fetch.js`
   *   (`applyFmdContext`, appelé depuis `toResult`), donc avant même que la promesse ne se
   *   résolve. Chercher le patch FMD ici est une impasse.
   * @returns {object|null}
   */
  function createStates(options) {
    var config = options || {};
    var host = config.host;
    if (!host || !host.input || !host.wrapper || !host.results) return null;

    var input = host.input;
    var wrapper = host.wrapper;
    var results = host.results;
    /** Absent tant que T30/T31/T32 n'ont pas posé l'état A : le module tient sans lui. */
    var empty = host.empty || null;

    var state = {
      /**
       * Dernier terme traité. Naît avec la valeur du champ — c'est la moitié du garde
       * Q-S11 — et retombe à `''` après une erreur, pour que retaper le même terme
       * reparte (le legacy posait son `currentString` AVANT le fetch et ne le remettait
       * jamais à zéro : le terme restait ignoré même le verrou levé).
       */
      term: normalize(input.value),
      /** Verrou de requête, équivalent du `isLoading` legacy. Relâché sur TOUTE sortie. */
      loading: false,
      /** L'état B porte-t-il une réponse insérée ? Décide de l'état montré à l'ouverture. */
      hasResults: false,
      /** Sérial de requête : une réponse d'un rang périmé ne touche plus à l'affichage. */
      requestId: 0,
      listeners: [],
    };

    function listen(target, type, handler) {
      target.addEventListener(type, handler);
      state.listeners.push({ target: target, type: type, handler: handler });
    }

    /**
     * La bascule, et le seul endroit qui écrit `hidden` sur les deux nœuds d'état : ils
     * s'excluent toujours, dans ce sens comme dans l'autre.
     */
    function show(showResults) {
      results.hidden = !showResults;
      if (empty) empty.hidden = showResults;
    }

    function setLoading(active) {
      state.loading = !!active;
      wrapper.classList.toggle(LOADING_CLASS, state.loading);
    }

    /**
     * État A. Le contenu de B est jeté avec lui : une réponse périmée ne doit pas
     * ressurgir à la réouverture, et ses `<greenleaze-rental-options>` doivent être
     * défaits. Le backspace reste gratuit — le cache du fetcher garde la CHAÎNE, pas
     * le document, et re-sert sans réseau.
     */
    function showEmptyState() {
      state.hasResults = false;
      results.textContent = '';
      show(false);
    }

    /** Retour à A + verrou relâché + terme remis à zéro. Sortie commune du champ vidé et de l'erreur. */
    function resetToEmpty() {
      if (host.fetcher) host.fetcher.abort();
      setLoading(false);
      state.term = '';
      showEmptyState();
    }

    /**
     * Insertion PUIS bascule, jamais l'inverse : c'est ce qui supprime le flash. Une
     * réponse sans nœud de panneau est traitée comme une erreur, pas comme un vide —
     * un vide légitime arrive avec son panneau et son message `no_results` (T13).
     */
    function applyResult(result) {
      if (!result || !result.panel) return false;
      if (typeof config.beforeInsert === 'function') config.beforeInsert(result);
      results.textContent = '';
      results.appendChild(result.panel);
      state.hasResults = true;
      show(true);
      return true;
    }

    /** Erreur réelle : retour à l'état A, verrou relâché, aucun message montré au visiteur. */
    function fail() {
      setLoading(false);
      state.term = '';
      showEmptyState();
    }

    /**
     * Une intention de recherche. Le verrou est posé avant l'appel et relâché sur les
     * trois sorties — réponse, vide, erreur. L'annulation, elle, ne relâche rien : la
     * frappe qui a annulé a déjà posé son propre verrou et porte un rang plus récent.
     */
    function runSearch(term) {
      var query = normalize(term);
      if (!query) {
        resetToEmpty();
        return;
      }
      state.term = query;
      if (!host.fetcher) return;

      var id = (state.requestId += 1);
      setLoading(true);

      host.fetcher
        .search(query)
        .then(function (result) {
          if (id !== state.requestId) return;
          setLoading(false);
          if (!result) {
            showEmptyState();
            return;
          }
          if (!applyResult(result)) fail();
        })
        .catch(function (error) {
          /** Annulation = fonctionnement normal (une frappe annule la précédente). */
          if (error && error.name === 'AbortError') return;
          if (id !== state.requestId) return;
          fail();
        });
    }

    /**
     * Seule porte d'entrée d'une requête. Deux gardes, et ils sont indépendants : un
     * `input` synthétique est écarté par `isTrusted`, un `input` qui ne change pas la
     * valeur l'est par la comparaison — sur `/search?q=cannondale`, les deux répondent.
     */
    function onInput(event) {
      if (event && event.isTrusted === false) return;
      var query = normalize(input.value);
      if (query === state.term) return;
      if (!query) {
        resetToEmpty();
        return;
      }
      runSearch(query);
    }

    /**
     * Ouverture : on re-rend les récentes (une autre instance, ou un autre onglet, a pu
     * écrire) et on montre l'état qui correspond à ce que le panneau détient. Aucune
     * requête ne part d'ici — c'est la seconde moitié du garde Q-S11.
     */
    function onOpen() {
      if (host.recent) host.recent.render();
      if (!state.hasResults) show(false);
    }

    /** Fermeture pendant une requête : elle ne sert plus personne, et le verrou doit tomber. */
    function onClose() {
      if (!state.loading) return;
      if (host.fetcher) host.fetcher.abort();
      setLoading(false);
      state.term = '';
    }

    /**
     * Clic sur une ligne récente (T26). L'événement est annulable et on l'annule quand même :
     * le `href` de la ligne est `/search` NU, sans `q` — le laisser passer enverrait sur une
     * page de résultats vide. On reconstruit donc la navigation depuis le formulaire.
     *
     * DÉCISION UTILISATEUR 2026-09-16 — on NAVIGUE, on ne se contente plus de rejouer le
     * panneau. La version précédente remplissait le champ et relançait la suggestion : le
     * terme réapparaissait dans le panneau sans jamais mener aux résultats, ce qui se lit
     * comme « ça remplit la barre mais ne lance pas la recherche ». Une entrée récente est
     * une recherche qu'on veut REFAIRE, pas un brouillon à retoucher.
     *
     * `requestSubmit()` et jamais `submit()` : seul le premier déclenche l'event `submit`,
     * dont dépendent le traitement de query-string (`view=parcours-fmd`, T42) et
     * l'enregistrement de la recherche (`onSubmit` ci-dessous). Repli sur `runSearch` si
     * le formulaire manque ou si le navigateur n'expose pas `requestSubmit` — le panneau
     * reste alors utile plutôt que le clic ne fasse rien.
     */
    function onRecentSelect(event) {
      var term = normalize(event && event.detail && event.detail.term);
      if (!term) return;
      event.preventDefault();
      input.value = term;
      if (host.recent) host.recent.add(term);
      if (document.activeElement !== input) input.focus();
      if (host.form && typeof host.form.requestSubmit === 'function') {
        host.form.requestSubmit();
        return;
      }
      runSearch(term);
    }

    /** Soumission = recherche aboutie. Écriture synchrone, donc acquise avant la navigation. */
    function onSubmit() {
      if (host.recent) host.recent.add(input.value);
    }

    function destroy() {
      state.listeners.forEach(function (entry) {
        entry.target.removeEventListener(entry.type, entry.handler);
      });
      state.listeners = [];
      state.requestId += 1;
      setLoading(false);
    }

    listen(input, 'input', onInput);
    listen(host, 'predictive-search:open', onOpen);
    listen(host, 'predictive-search:close', onClose);
    listen(host, 'predictive-search:recent-select', onRecentSelect);
    if (host.form) listen(host.form, 'submit', onSubmit);

    /**
     * État de naissance imposé au markup par T30/T31/T32 — B masqué, A visible. On le
     * REPOSE ici : une instance dont le markup naîtrait à l'envers afficherait ses deux
     * états ensemble à la première ouverture. Invisible au premier paint, le panneau
     * étant fermé (anti-pattern n°12).
     */
    show(false);

    return {
      state: state,
      show: show,
      showEmptyState: showEmptyState,
      showResults: applyResult,
      search: runSearch,
      destroy: destroy,
    };
  }

  registry.states = { create: createStates };
})(window, document);
